import React, { useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, List, ListOrdered, Underline, Link2, Undo2, Redo2 } from "lucide-react";

const SANITIZE_OPTS = {
  ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li", "a", "span", "div"],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

// Minimal contenteditable rich-text editor. Emits sanitized HTML.
export default function RichText({ value, onChange, placeholder = "Write a luxurious description…", testId = "richtext" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = DOMPurify.sanitize(value || "", SANITIZE_OPTS);
    }
    // eslint-disable-next-line
  }, []);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    handleInput();
  };
  const handleInput = () => onChange(DOMPurify.sanitize(ref.current?.innerHTML || "", SANITIZE_OPTS));
  const link = () => {
    const url = prompt("Link URL:");
    if (url) exec("createLink", url);
  };

  const TOOLS = [
    { i: Bold, cmd: "bold", label: "Bold" },
    { i: Italic, cmd: "italic", label: "Italic" },
    { i: Underline, cmd: "underline", label: "Underline" },
    { i: List, cmd: "insertUnorderedList", label: "Bulleted list" },
    { i: ListOrdered, cmd: "insertOrderedList", label: "Numbered list" },
  ];

  return (
    <div className="rounded-xl border border-white/10 bg-charcoal-800 overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-1 border-b border-white/5 px-2 py-1.5 bg-black/30">
        {TOOLS.map(({ i: I, cmd, label }) => (
          <button key={cmd} type="button" title={label} onClick={() => exec(cmd)} className="p-2 rounded hover:bg-white/5 hover:text-gold text-white/70" data-testid={`rt-${cmd}`}>
            <I className="w-3.5 h-3.5" />
          </button>
        ))}
        <button type="button" title="Link" onClick={link} className="p-2 rounded hover:bg-white/5 hover:text-gold text-white/70"><Link2 className="w-3.5 h-3.5" /></button>
        <span className="w-px h-4 bg-white/10 mx-1" />
        <button type="button" title="Undo" onClick={() => exec("undo")} className="p-2 rounded hover:bg-white/5 text-white/70"><Undo2 className="w-3.5 h-3.5" /></button>
        <button type="button" title="Redo" onClick={() => exec("redo")} className="p-2 rounded hover:bg-white/5 text-white/70"><Redo2 className="w-3.5 h-3.5" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        className="min-h-[120px] px-4 py-3 outline-none text-white prose-invert leading-relaxed rich-editor"
      />
      <style>{`
        .rich-editor:empty:before { content: attr(data-placeholder); color: rgba(255,255,255,0.3); pointer-events: none; }
        .rich-editor ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        .rich-editor ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
        .rich-editor a { color: #D4AF37; text-decoration: underline; }
        .rich-editor strong { color: #fff; font-weight: 600; }
      `}</style>
    </div>
  );
}
